import { useState } from 'react';
import { X, ChevronLeft, ChevronRight, Star } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

interface PhotoLightboxProps {
  photos: string[];
  initialIndex: number;
  onClose: () => void;
  onSetHero?: (photoUrl: string) => void;
  heroPhotoUrl?: string | null;
  isOwner?: boolean;
}

const PhotoLightbox = ({ 
  photos, 
  initialIndex, 
  onClose, 
  onSetHero,
  heroPhotoUrl,
  isOwner 
}: PhotoLightboxProps) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') handlePrevious();
    if (e.key === 'ArrowRight') handleNext();
  };

  const currentPhoto = photos[currentIndex];
  const isHeroPhoto = currentPhoto === heroPhotoUrl;

  return (
    <div 
      className="fixed inset-0 z-50 bg-background/95 backdrop-blur-sm flex items-center justify-center"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      {/* Close button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 right-4 z-50"
        onClick={onClose}
      >
        <X className="h-6 w-6" />
      </Button>

      {/* Navigation buttons */}
      {photos.length > 1 && (
        <>
          <Button
            variant="ghost"
            size="icon"
            className="absolute left-4 top-1/2 -translate-y-1/2 z-50"
            onClick={(e) => {
              e.stopPropagation();
              handlePrevious();
            }}
          >
            <ChevronLeft className="h-8 w-8" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="absolute right-4 top-1/2 -translate-y-1/2 z-50"
            onClick={(e) => {
              e.stopPropagation();
              handleNext();
            }}
          >
            <ChevronRight className="h-8 w-8" />
          </Button>
        </>
      )}

      {/* Image container */}
      <div 
        className="relative max-w-7xl max-h-[90vh] mx-auto px-16"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={currentPhoto}
          alt={`Photo ${currentIndex + 1}`}
          className="max-w-full max-h-[90vh] object-contain rounded-lg photo-signature"
        />

        {/* Photo info bar */}
        <div className="absolute bottom-0 left-0 right-0 bg-background/80 backdrop-blur-sm p-4 rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="secondary">
                {currentIndex + 1} / {photos.length}
              </Badge>
              {isHeroPhoto && (
                <Badge variant="default" className="gap-1">
                  <Star className="h-3 w-3 fill-current" />
                  Hero Photo
                </Badge>
              )}
            </div>
            {isOwner && onSetHero && !isHeroPhoto && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSetHero(currentPhoto)}
              >
                <Star className="h-4 w-4 mr-2" />
                Set as Hero Photo
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PhotoLightbox;
